import axios from 'axios';
import Vue from 'vue';
import secureStorage from '@/utils/secureStorage';
import { API } from '@/config/api';

const state = {
    isRecording: false,
    stream: null,
    mediaRecorder: null,
    chunks: [],
    transcript: null,
    minimized: false,
    folderName: '',
    startTime: null,
    elapsedTime: 0,
    recordedVideoUrl: null,
    timerInterval: null,
    diarization: false,
};

const getters = {
    isRecording: (state) => state.isRecording,
    isMinimized: (state) => state.minimized,
    transcript: (state) => state.transcript,
    folderName: (state) => state.folderName,
    elapsedTime: (state) => state.elapsedTime,
    recordedVideoUrl: (state) => state.recordedVideoUrl,
};

const mutations = {
    SET_RECORDING(state, value) {
        state.isRecording = value;
    },
    SET_STREAM(state, stream) {
        state.stream = stream;
    },
    SET_MEDIA_RECORDER(state, recorder) {
        state.mediaRecorder = recorder;
    },
    ADD_CHUNK(state, chunk) {
        state.chunks.push(chunk);
    },
    CLEAR_CHUNKS(state) {
        state.chunks = [];
    },
    SET_TRANSCRIPT(state, transcript) {
        state.transcript = transcript;
    },
    SET_MINIMIZED(state, value) {
        state.minimized = value;
    },
    SET_FOLDER_NAME(state, name) {
        state.folderName = name;
    },
    SET_START_TIME(state, time) {
        state.startTime = time;
    },
    SET_ELAPSED_TIME(state, time) {
        state.elapsedTime = time;
    },
    SET_RECORDED_VIDEO_URL(state, url) {
        state.recordedVideoUrl = url;
    },
    SET_TIMER_INTERVAL(state, interval) {
        state.timerInterval = interval;
    },
    SET_DIARIZATION(state, value) {
        state.diarization = value;
    },
};

const actions = {
    async startRecording({ commit, state, dispatch }, { name: folderName, diarization = false } = {}) {
        try {
            if (!folderName || folderName.trim() === '') {
                Vue.$toast('Please enter a folder name before starting recording', { type: 'warning' });
                return false;
            }

            // Clear previous recording data
            commit('CLEAR_CHUNKS');
            commit('SET_FOLDER_NAME', folderName);
            commit('SET_DIARIZATION', diarization);
            commit('SET_TRANSCRIPT', null);

            // Request screen capture
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    cursor: "always"
                },
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100,
                },
            });

            commit('SET_STREAM', stream);

            // Create MediaRecorder
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'video/webm; codecs=vp9',
                audioBitsPerSecond: 128000,
            });

            // Handle data available
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    commit('ADD_CHUNK', event.data);
                }
            };

            // Handle stop
            mediaRecorder.onstop = async () => {
                const blob = new Blob(state.chunks, { type: 'video/webm' });

                // Stop timer
                if (state.timerInterval) {
                    clearInterval(state.timerInterval);
                    commit('SET_TIMER_INTERVAL', null);
                }

                // Create URL for video playback
                const videoUrl = URL.createObjectURL(blob);
                commit('SET_RECORDED_VIDEO_URL', videoUrl);

                // Stop all tracks
                if (state.stream) {
                    state.stream.getTracks().forEach((track) => track.stop());
                    commit('SET_STREAM', null);
                }

                // Send for transcription
                await dispatch('sendToTranscription', blob);

                // Reset state
                commit('SET_RECORDING', false);
                commit('SET_MINIMIZED', false);
                commit('CLEAR_CHUNKS');
            };

            // Handle stream ending (user stops sharing)
            stream.getVideoTracks()[0].onended = () => {
                if (state.isRecording) {
                    dispatch('stopRecording');
                }
            };

            commit('SET_MEDIA_RECORDER', mediaRecorder);

            mediaRecorder.start();

            commit('SET_RECORDING', true);
            commit('SET_START_TIME', Date.now());

            // Start timer
            dispatch('startTimer');

            Vue.$toast('Screen recording started!', { type: 'success' });
            return true;
        } catch (error) {

            // More detailed error message
            if (error.name === 'NotAllowedError') {
                Vue.$toast('Screen recording permission denied', { type: 'error' });
            } else if (error.name === 'NotFoundError') {
                Vue.$toast('No screen available for recording', { type: 'error' });
            } else if (error.name === 'NotSupportedError') {
                Vue.$toast('Screen recording not supported in your browser', { type: 'error' });
            } else {
                Vue.$toast('Failed to start recording: ' + error.message, { type: 'error' });
            }

            return false;
        }
    },

    stopRecording({ commit, state }) {
        if (state.mediaRecorder && state.isRecording) {
            state.mediaRecorder.stop();
            Vue.$toast('Recording stopped. Processing...', { type: 'info' });
        }
    },

    minimizeRecorder({ commit }) {
        commit('SET_MINIMIZED', true);
    },

    restoreRecorder({ commit }) {
        commit('SET_MINIMIZED', false);
    },

    async sendToTranscription({ commit, state }, blob) {
        try {
            const user = secureStorage.getItem('user');
            const userIdAsArray = JSON.stringify([user.id]);

            const groupIds = user.groups.map((group) => group.id);
            const groupIdsAsArray = JSON.stringify(groupIds);

            const formData = new FormData();
            formData.append('file', blob, 'screen-recording.webm');
            formData.append('typeOfTranscription', '3');
            formData.append('originalLanguage', 'Serbian');
            formData.append('userId', user.id);
            formData.append('groupIds', groupIdsAsArray);
            formData.append('userIds', userIdAsArray);
            formData.append('liveTranscriptionGroupName', state.folderName);
            formData.append('companieId', user.company_id);
            if (state.diarization) formData.append('dz', 'true');

            const response = await axios.post(API.TRANSCRIBE, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            commit('SET_TRANSCRIPT', response.data);
            Vue.$toast('Your recording has been successfully submitted for transcription!', { type: 'success' });
        } catch (error) {
            
            Vue.$toast('Failed to submit recording for transcription.', { type: 'error' });
        }
    },

    startTimer({ commit, state }) {
        const interval = setInterval(() => {
            if (!state.isRecording) {
                clearInterval(interval);
                commit('SET_TIMER_INTERVAL', null);
                return;
            }
            const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
            commit('SET_ELAPSED_TIME', elapsed);
        }, 1000);
        commit('SET_TIMER_INTERVAL', interval);
    },

    resetRecorder({ commit, state }) {
        // Clear timer if active
        if (state.timerInterval) {
            clearInterval(state.timerInterval);
            commit('SET_TIMER_INTERVAL', null);
        }

        // Revoke video URL to free memory
        if (state.recordedVideoUrl) {
            URL.revokeObjectURL(state.recordedVideoUrl);
            commit('SET_RECORDED_VIDEO_URL', null);
        }

        commit('SET_RECORDING', false);
        commit('SET_STREAM', null);
        commit('SET_MEDIA_RECORDER', null);
        commit('CLEAR_CHUNKS');
        commit('SET_TRANSCRIPT', null);
        commit('SET_MINIMIZED', false);
        commit('SET_FOLDER_NAME', '');
        commit('SET_ELAPSED_TIME', 0);
        commit('SET_DIARIZATION', false);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
